import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { Button, Input, Tooltip, Typography, theme as antdTheme } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { EditOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { space } from '@viq-admin/ui';
import type { BatchLead, BatchLeadNotesApi, BatchNote } from './types';
import { NOTES_COPY, NOTE_TEXT_MAX, earlierNotesCount, noteSaveFailureMessage } from './leadNotes';
import { createLeadNoteSyncStore, type LeadNoteSyncStore, type NoteSaveKind } from './useLeadNoteSync';

/**
 * The Batch grid's Notes cell (port plan step 5, the prototype's look): the
 * lead's latest rep note, two lines at most with the full text on hover.
 *
 * Editable only when the table hands it `api` (the shell's `leadNotesApi`,
 * on the Active tab). Then click, Enter or Space opens a text box in place
 * that grows with the text; clicking away or Cmd/Ctrl+Enter saves, Esc
 * cancels. An unchanged or emptied box just closes: clearing a note never
 * deletes it. A lead with no note reads "+ Add a note", which creates one;
 * earlier notes stay separate records and open with the lead ("+N earlier
 * notes"). The cell saves through `api`, by way of `noteSync`, and hands the
 * saved note to `onSaved`; a failed save keeps the box open with the text
 * and says why. An edit keeps the version it opened on through every retry:
 * after "Someone else changed this note" a retry conflicts again rather than
 * overwriting the newer note a refresh may have shown meanwhile.
 *
 * In the grid `noteSync` is the screen's store, so a save outlives the cell:
 * a cell that mounts while its lead's save is still out (the table came back
 * from a CRM tab) shows that save exactly as the cell that started it did —
 * the text, read-only, "Saving…" — and takes its outcome; one that mounts
 * after such a save failed reopens with the text and the reason.
 */

export interface NotesCellProps {
  lead: Pick<BatchLead, 'id' | 'latestNote' | 'notesCount'>;
  /** The row's display name, for accessible names ("Edit note for Jane Buyer"). */
  leadName: string;
  /** Present → editable. */
  api?: BatchLeadNotesApi;
  /**
   * A save this cell started succeeded; `created` when it made the lead's
   * first shown note. The grid leaves it out: its `noteSync` lays every
   * confirmed save over the rows itself, including one that lands after this
   * cell unmounted.
   */
  onSaved?: (note: BatchNote, created: boolean) => void;
  /** Opens the lead (the drawer), where every note is. */
  onOpenLead: () => void;
  /**
   * The store the saves go through (the grid passes the screen's, so a save
   * in flight survives the table unmounting). Absent → the cell's own.
   */
  noteSync?: LeadNoteSyncStore;
  /** The event a save is made under (its KPI tiles refresh after the burst). */
  conferenceId?: string | null;
}

/** Two lines, then an ellipsis. */
const CLAMP: CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  minWidth: 0,
};

/** A DOM id for aria wiring, sanitised as EditableCells does (jsdom selectors). */
function useDomId(prefix: string): string {
  return `${prefix}-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
}

export function NotesCell({
  lead,
  leadName,
  api,
  onSaved,
  onOpenLead,
  noteSync,
  conferenceId,
}: NotesCellProps) {
  const { token } = antdTheme.useToken();
  const note = lead.latestNote ?? null;
  const text = note?.text ?? '';
  const [ownSync] = useState(() => createLeadNoteSyncStore());
  const sync = noteSync ?? ownSync;
  // What an earlier cell for this lead left in the store: a save still out,
  // or one that failed with the box gone. This cell opens on it — never on
  // "Add a note" while a create is out, which a second create would duplicate.
  const [carried] = useState(() => {
    if (!api) return null;
    const out = sync.inFlight(lead.id);
    if (out) return { kind: out.kind, base: out.base, text: out.text, promise: out.promise, message: null };
    const failed = sync.failure(lead.id);
    return failed
      ? { kind: failed.kind, base: failed.base, text: failed.text, promise: null, message: failed.message }
      : null;
  });
  const [editing, setEditing] = useState(carried !== null);
  const [draft, setDraft] = useState(carried?.text ?? '');
  const [busy, setBusy] = useState(!!carried?.promise);
  const [error, setError] = useState<string | null>(carried?.message ?? null);
  // True whenever no edit is open, so the blur that follows Esc, a keyboard
  // save or the box unmounting can't save again.
  const closedRef = useRef(carried === null);
  // A save is out. Cmd+Enter then a blur (or two blurs) must not send twice.
  const savingRef = useRef(!!carried?.promise);
  // The note this edit started from — set when the box opens (or carried
  // over with the save), never from a row loaded since. Every save of this
  // edit, retries included, sends its version: after a 409 a retry can only
  // conflict again, never overwrite a colleague's newer note.
  const baseRef = useRef<BatchNote | null>(carried ? carried.base : null);
  const refocusRef = useRef(false);
  // Focus the box only when the operator opened it, never when this cell
  // mounted onto a carried save (that would pull focus into the grid).
  const focusBoxRef = useRef(false);
  // A save's outcome may land after this cell unmounted; then only the store
  // (and `onSaved`) hear of it.
  const mountedRef = useRef(true);
  const displayRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<TextAreaRef>(null);
  const textId = useDomId('viq-note-text');
  const hintId = useDomId('viq-note-hint');
  const errorId = useDomId('viq-note-error');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (editing) {
      if (!focusBoxRef.current) return;
      focusBoxRef.current = false;
      const el = boxRef.current?.resizableTextArea?.textArea;
      el?.focus();
      el?.setSelectionRange(el.value.length, el.value.length);
    } else if (refocusRef.current) {
      refocusRef.current = false;
      displayRef.current?.focus();
    }
  }, [editing]);

  // A carried save still out: take its outcome as the cell that started it
  // would have. Once, for what this cell mounted with.
  useEffect(() => {
    if (carried?.promise) void settle(carried.promise, carried.kind, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function open() {
    baseRef.current = note;
    setDraft(text);
    setError(null);
    closedRef.current = false;
    focusBoxRef.current = true;
    setEditing(true);
  }

  function close(refocus: boolean) {
    closedRef.current = true;
    refocusRef.current = refocus;
    setError(null);
    setEditing(false);
    // A failure the box was still showing is dealt with.
    sync.clearFailure(lead.id);
  }

  /** The outcome of a save, for this cell while it is mounted. */
  async function settle(promise: Promise<BatchNote>, kind: NoteSaveKind, refocus: boolean) {
    try {
      const saved = await promise;
      onSaved?.(saved, kind === 'create');
      if (mountedRef.current) close(refocus);
    } catch (err) {
      if (mountedRef.current) setError(noteSaveFailureMessage(err, kind));
    } finally {
      if (mountedRef.current) {
        savingRef.current = false;
        setBusy(false);
      }
    }
  }

  async function commit(refocus: boolean) {
    if (!api || closedRef.current || savingRef.current) return;
    const base = baseRef.current;
    const next = draft.trim();
    // Clearing a note isn't a delete, and an unchanged one needs no call —
    // unchanged from where this edit started.
    if (next === '' || next === (base?.text ?? '').trim()) {
      close(refocus);
      return;
    }
    if (next.length > NOTE_TEXT_MAX) {
      setError(NOTES_COPY.tooLong);
      return;
    }
    savingRef.current = true;
    setBusy(true);
    setError(null);
    await settle(sync.save(api, lead.id, base, next, { conferenceId }), base ? 'edit' : 'create', refocus);
  }

  const earlier = earlierNotesCount(lead);
  const earlierLink =
    earlier > 0 ? (
      <Button
        type="link"
        size="small"
        aria-label={`${NOTES_COPY.earlier(earlier)} for ${leadName}`}
        onClick={onOpenLead}
        style={{ padding: 0, height: 'auto', fontSize: token.fontSizeSM }}
      >
        {NOTES_COPY.earlier(earlier)}
      </Button>
    ) : null;

  if (api && editing) {
    return (
      <div>
        <Tooltip
          open={!!error}
          title={
            error ? (
              <span id={errorId} role="alert">
                {error}
              </span>
            ) : null
          }
          color={token.colorError}
          placement="bottom"
        >
          <Input.TextArea
            ref={boxRef}
            value={draft}
            // Read-only, not disabled, while the save is out: focus stays put,
            // so a failed Cmd+Enter leaves the operator typing where they were.
            readOnly={busy}
            aria-busy={busy || undefined}
            autoSize={{ minRows: 3, maxRows: 10 }}
            aria-label={`Note for ${leadName}`}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${hintId} ${errorId}` : hintId}
            status={error ? 'error' : undefined}
            placeholder={NOTES_COPY.placeholder}
            onChange={(e) => {
              setDraft(e.target.value);
              if (error) setError(null);
            }}
            onBlur={() => void commit(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                // Never while a save is out: its outcome must still show here.
                if (!savingRef.current) close(true);
              } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void commit(true);
              }
            }}
          />
        </Tooltip>
        <Typography.Text type="secondary" id={hintId} style={{ fontSize: token.fontSizeSM }}>
          {busy ? (
            <>
              <LoadingOutlined aria-hidden data-testid="note-saving" /> {NOTES_COPY.saving}
            </>
          ) : (
            NOTES_COPY.hint
          )}
        </Typography.Text>
      </div>
    );
  }

  if (!api) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
        {text ? (
          <span title={text} style={CLAMP}>
            {text}
          </span>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        )}
        {earlierLink}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
      <div
        ref={displayRef}
        role="button"
        tabIndex={0}
        className="viq-inline-edit"
        aria-label={text ? `Edit note for ${leadName}` : `${NOTES_COPY.addNote} for ${leadName}`}
        aria-describedby={text ? textId : undefined}
        title={text || undefined}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        }}
        style={{ display: 'flex', alignItems: 'flex-start', width: '100%', minWidth: 0, cursor: 'text' }}
      >
        {text ? (
          <span id={textId} style={CLAMP}>
            {text}
          </span>
        ) : (
          <span style={{ color: token.colorLink, whiteSpace: 'nowrap' }}>
            <PlusOutlined aria-hidden style={{ marginInlineEnd: space.xs }} />
            {NOTES_COPY.addNote}
          </span>
        )}
        {text && (
          <EditOutlined
            aria-hidden
            className="viq-inline-edit-pencil"
            style={{ color: token.colorTextTertiary, flexShrink: 0, marginTop: 3 }}
          />
        )}
      </div>
      {earlierLink}
    </div>
  );
}
